/**
 * E007-T022: InputMappingGrid Component
 *
 * Schema-based input mapping grid for automation sidebar.
 * Maps workflow schema fields to source table columns.
 * Shows required/optional status and type compatibility.
 *
 * Falls back to ColumnSelector when no schema is available.
 */

import { notify, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import type { InputMapping } from '@streamlain/field-types';
import { checkCompatibility } from '@streamlain/field-types';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { ColumnInfo } from '../../services/database-discovery';
import type { WorkflowSchemaField } from '../../services/workflow-client';
import {
  WEBHOOK_PAYLOAD_COLUMN_ID,
  WEBHOOK_PAYLOAD_TABLE_ID,
} from '../../types';
import { AutoMatchDialog } from './auto-match-dialog';
import {
  SectionEmpty,
  SectionMappingHeader,
  SectionMappingRow,
  SectionStack,
} from './section-layout';
import { CompatDot, TypeChip } from './type-chip';
import type { FieldOptionGroup } from './typed-field-select';
import { TypedFieldSelect } from './typed-field-select';

const AUTOMATION_MAPPING_LIMIT = 32;
const INPUT_MAPPING_GRID_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr) 16px';

// ============================================================================
// Types
// ============================================================================

export type { InputMapping };

/** sl-m379: Column group from a specific input table */
export interface ColumnGroup {
  /** Stable InputTable.id used for inputMappings[].inputTableId */
  inputTableId: string;
  /** Physical database/table block id (kept for compatibility/UI grouping) */
  tableId?: string;
  /** Display label (e.g. "Lead", "Prompts", "Kunden") */
  label: string;
  /** Columns from this table */
  columns: ColumnInfo[];
  /** sl-i6t9: Relation cardinality — 'many' produces array fields in payload */
  cardinality?: 'one' | 'many';
}

interface InputMappingGridProps {
  /** Schema input fields (required + optional) */
  schemaFields: {
    required: WorkflowSchemaField[];
    optional: WorkflowSchemaField[];
  };
  /** Available source columns (lead table — backwards compatible) */
  sourceColumns: ColumnInfo[];
  /** Current input mappings */
  mappings: InputMapping[];
  /** Callback when mappings change */
  onMappingsChange: (mappings: InputMapping[]) => void;
  /** sl-m379: Additional column groups from secondary tables */
  secondaryColumnGroups?: ColumnGroup[];
  /** Display name for the source (lead) table */
  sourceTableName?: string;
  /** When set, adds a "Webhook: <name>" group with a single "payload" (json) option */
  webhookGroup?: { name: string };
}

// (CompatibilityBadge and Menu config moved to shared TypedFieldSelect)

// ============================================================================
// Main Component
// ============================================================================

export const InputMappingGrid: FC<InputMappingGridProps> = ({
  schemaFields,
  sourceColumns,
  mappings,
  onMappingsChange,
  secondaryColumnGroups,
  sourceTableName,
  webhookGroup,
}) => {
  const t = useI18n();
  const [isAutoMatchOpen, setIsAutoMatchOpen] = useState(false);

  // All fields combined with required flag
  const allFields = useMemo(() => {
    const required = schemaFields.required.map(f => ({
      ...f,
      isRequired: true,
    }));
    const optional = schemaFields.optional.map(f => ({
      ...f,
      isRequired: false,
    }));
    return [...required, ...optional];
  }, [schemaFields]);

  // Build mapping lookup: schemaFieldId → { columnId, inputTableId }
  const mappingLookup = useMemo(() => {
    const map = new Map<
      string,
      { columnId: string | null; inputTableId?: string }
    >();
    for (const m of mappings) {
      map.set(m.schemaFieldId, {
        columnId: m.columnId,
        inputTableId: m.inputTableId,
      });
    }
    return map;
  }, [mappings]);

  // Get column by ID + optional inputTableId for disambiguation
  const getColumn = useCallback(
    (columnId: string | null, inputTableId?: string): ColumnInfo | null => {
      if (!columnId) return null;
      // Webhook payload sentinel
      if (inputTableId === WEBHOOK_PAYLOAD_TABLE_ID) {
        return {
          id: WEBHOOK_PAYLOAD_COLUMN_ID,
          name: WEBHOOK_PAYLOAD_COLUMN_ID,
          type: 'json',
        } as ColumnInfo;
      }
      // If inputTableId is explicitly '' or undefined, search lead first
      if (!inputTableId || inputTableId === '') {
        const fromLead = sourceColumns.find(c => c.id === columnId);
        if (fromLead) return fromLead;
      }
      // Search secondary groups (scoped by inputTableId if provided)
      if (secondaryColumnGroups) {
        for (const group of secondaryColumnGroups) {
          if (
            inputTableId &&
            inputTableId !== '' &&
            group.inputTableId !== inputTableId
          )
            continue;
          const fromGroup = group.columns.find(c => c.id === columnId);
          if (fromGroup) return fromGroup;
        }
      }
      // Fallback: search everything
      const fromLead = sourceColumns.find(c => c.id === columnId);
      if (fromLead) return fromLead;
      return null;
    },
    [sourceColumns, secondaryColumnGroups]
  );

  // Handle mapping change — inputTableId is passed explicitly to avoid
  // ambiguity when multiple tables share the same column ID (e.g. 'title').
  const handleMappingChange = useCallback(
    (schemaFieldId: string, columnId: string | null, inputTableId?: string) => {
      const field = allFields.find(f => f.id === schemaFieldId);
      const newMappings = [...mappings];
      const existingIndex = newMappings.findIndex(
        m => m.schemaFieldId === schemaFieldId
      );

      if (existingIndex >= 0) {
        if (columnId === null) {
          newMappings.splice(existingIndex, 1);
        } else {
          newMappings[existingIndex] = {
            schemaFieldId,
            schemaFieldName: field?.field,
            columnId,
            inputTableId,
          };
        }
      } else if (columnId !== null) {
        if (newMappings.length >= AUTOMATION_MAPPING_LIMIT) {
          notify.warning({
            title: 'Mapping limit reached',
            message: `Automations support up to ${AUTOMATION_MAPPING_LIMIT} input mappings.`,
          });
          return;
        }
        newMappings.push({
          schemaFieldId,
          schemaFieldName: field?.field,
          columnId,
          inputTableId,
        });
      }

      onMappingsChange(newMappings);
    },
    [allFields, mappings, onMappingsChange]
  );

  // Build FieldOptionGroup[] from source columns + secondary groups
  const columnGroups = useMemo((): FieldOptionGroup[] => {
    const groups: FieldOptionGroup[] = [];
    // Webhook source group (prepended — shown first in dropdown)
    if (webhookGroup) {
      groups.push({
        id: WEBHOOK_PAYLOAD_TABLE_ID,
        label: `Webhook: ${webhookGroup.name}`,
        options: [
          {
            id: `${WEBHOOK_PAYLOAD_TABLE_ID}::${WEBHOOK_PAYLOAD_COLUMN_ID}`,
            label: WEBHOOK_PAYLOAD_COLUMN_ID,
            type: 'json',
          },
        ],
      });
    }
    // Lead table group
    if (sourceColumns.length > 0) {
      groups.push({
        id: '_lead',
        label: sourceTableName || 'Source',
        options: sourceColumns.map(col => ({
          id: col.id,
          label: col.name,
          type: col.type,
        })),
      });
    }
    // Secondary table groups
    if (secondaryColumnGroups) {
      for (const group of secondaryColumnGroups) {
        const cardLabel = group.cardinality === 'many' ? ` (1:N)` : '';
        groups.push({
          id: group.inputTableId,
          label: `${group.label}${cardLabel}`,
          options: group.columns.map(col => ({
            id: `${group.inputTableId}::${col.id}`,
            label: col.name,
            type: col.type,
          })),
        });
      }
    }
    return groups;
  }, [sourceColumns, secondaryColumnGroups, sourceTableName, webhookGroup]);

  // Count missing required fields
  const missingRequired = useMemo(() => {
    return schemaFields.required.filter(
      f =>
        !mappingLookup.has(f.id) || mappingLookup.get(f.id)?.columnId === null
    );
  }, [schemaFields.required, mappingLookup]);

  if (allFields.length === 0) {
    return (
      <SectionEmpty>
        {t['com.streamlain.streamlain.input.no-schema-fields']()}
      </SectionEmpty>
    );
  }

  const hasAutoMapData = sourceColumns.length > 0 && allFields.length > 0;

  return (
    <SectionStack>
      {/* Auto-Match Dialog */}
      {isAutoMatchOpen && (
        <AutoMatchDialog
          open={isAutoMatchOpen}
          onClose={() => setIsAutoMatchOpen(false)}
          sourceFields={sourceColumns.map(c => ({
            path: c.id,
            type: c.type,
            displayName: c.name,
          }))}
          targetFields={allFields.map(f => ({
            id: f.id,
            name: f.field,
            type: f.type,
          }))}
          existingTargetIds={
            new Set(mappings.filter(m => m.columnId).map(m => m.schemaFieldId))
          }
          sourceLabel={t['com.streamlain.streamlain.mapping.source-column']()}
          targetLabel={t['com.streamlain.streamlain.mapping.schema-field']()}
          onApply={suggestions => {
            const matchedIds = new Set(suggestions.map(s => s.targetColumnId));
            const preserved = mappings.filter(
              m => !matchedIds.has(m.schemaFieldId)
            );
            const availableSlots = Math.max(
              0,
              AUTOMATION_MAPPING_LIMIT - preserved.length
            );
            const limitedSuggestions = suggestions.slice(0, availableSlots);
            if (limitedSuggestions.length < suggestions.length) {
              notify.warning({
                title: 'Mapping limit reached',
                message: `Automations support up to ${AUTOMATION_MAPPING_LIMIT} input mappings.`,
              });
            }
            const newMappings = limitedSuggestions.map(s => {
              const field = allFields.find(f => f.id === s.targetColumnId);
              return {
                schemaFieldId: s.targetColumnId,
                schemaFieldName: field?.field,
                columnId: s.responseField,
              };
            });
            onMappingsChange([...preserved, ...newMappings]);
          }}
        />
      )}

      {/* Header - matches output field-mapping-grid style */}
      <SectionMappingHeader columns={INPUT_MAPPING_GRID_COLUMNS}>
        <span>{t['com.streamlain.streamlain.mapping.source-column']()}</span>
        <span>
          {t['com.streamlain.streamlain.mapping.schema-field']()}
          {missingRequired.length > 0 && (
            <span
              style={{
                marginLeft: '6px',
                fontSize: '10px',
                color: '#F44336',
                fontWeight: 600,
              }}
            >
              (
              {t['com.streamlain.streamlain.mapping.missing-count']({
                count: String(missingRequired.length),
              })}
              )
            </span>
          )}
        </span>
        <span></span>
      </SectionMappingHeader>

      {/* Field Rows */}
      {allFields.map(field => {
        const mapping = mappingLookup.get(field.id);
        const columnId = mapping?.columnId ?? null;
        const mappedTableId = mapping?.inputTableId;
        const column = getColumn(columnId, mappedTableId);
        const isMapped = columnId !== null;
        const compatibility = column
          ? checkCompatibility(field.type, column.type)
          : 'compatible';

        return (
          <SectionMappingRow
            key={field.id}
            columns={INPUT_MAPPING_GRID_COLUMNS}
          >
            {/* Column Dropdown (left) — shared TypedFieldSelect */}
            <TypedFieldSelect
              value={
                columnId
                  ? mappedTableId && mappedTableId !== ''
                    ? `${mappedTableId}::${columnId}`
                    : columnId
                  : ''
              }
              groups={columnGroups}
              onChange={optionId => {
                if (!optionId) {
                  handleMappingChange(field.id, null);
                } else if (optionId.includes('::')) {
                  const [tableId, colId] = optionId.split('::');
                  handleMappingChange(field.id, colId, tableId);
                } else {
                  handleMappingChange(field.id, optionId, '');
                }
              }}
              placeholder="Select"
              targetType={field.type}
            />

            {/* Schema Field (right) - static display */}
            <Tooltip content={field.description}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    fontWeight: field.isRequired ? 500 : 400,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {field.field}
                  {field.isRequired && (
                    <span style={{ color: '#ef4444', marginLeft: '2px' }}>
                      *
                    </span>
                  )}
                </span>
                <TypeChip type={field.type} style={{ marginLeft: 'auto' }} />
              </div>
            </Tooltip>

            {/* Compatibility/Status Indicator (right) */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isMapped ? (
                <CompatDot compatibility={compatibility} />
              ) : field.isRequired ? (
                <CompatDot compatibility="incompatible" />
              ) : null}
            </div>
          </SectionMappingRow>
        );
      })}

      {/* Auto-Map button */}
      {hasAutoMapData && (
        <button
          onClick={() => setIsAutoMatchOpen(true)}
          style={{
            width: '100%',
            padding: '6px 12px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: 'var(--streamlain-primary-color)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            marginTop: '4px',
          }}
        >
          {t['com.streamlain.streamlain.automap.button']()}
        </button>
      )}
    </SectionStack>
  );
};
